import { Injector } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';
import { PlanCardClickEvent } from 'src/app/components/new-billing/plan-card/plan-card.component';
import { ConcretePlan } from 'src/app/pages/new-billing/models/plan.model';
import { UpdateBillingDetails } from 'src/app/pages/new-billing/store/actions';
import { NewBillingState } from 'src/app/pages/new-billing/store/state';
import { BillingDetailsApiData } from 'src/app/pages/new-billing/types/api.types';
import { PricePeriodicity } from 'src/app/pages/new-billing/types/plan.types';
import { UpgradeConfirmModalTrackingContext } from 'src/app/pages/new-subscribe-plans/store/model';
import {
  UpgradeBillingResult
} from 'src/app/pages/new-billing/upgrade-billing-details/upgrade-billing-details.component';
import {
  UNLOCK_INSIGHTS_PLAN_UPGRADE_ORIGIN_UPSELL,
  UNLOCK_INSIGHTS_UPSELL_NAME
} from 'src/app/pages/dashboard/dashboard-team/unlock-insights-panel/unlock-insights.constants';
import { ApprovalsPlanService } from 'src/app/services/approvals/approvals-plan.service';
import { BillingActionsService } from 'src/app/services/billing/billing-actions.service';
import { BillingService } from 'src/app/services/billing/billing.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { CompanyService } from 'src/app/services/company/company.service';
import {
  getPlanDerivedCompanySettings as derivePlanCompanySettings,
  isBasicPlanId
} from 'src/app/services/company/plan-company-settings.util';
import { isUpsellSetupFlowEnabled } from 'src/app/constants/upsell-setup-flow.constants';
import { UpsellUpgradeContextService } from 'src/app/pages/upgrade/upsell-upgrade-context.service';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { UnlockInsightsUpgradeTrackingService } from 'src/app/services/unlock-insights-upgrade-tracking.service';
import { CompanySettings } from 'src/models';


type DialogGenericType = {
  isLoadingAction: boolean;
  errorMsg: string;
};

export type DialogOptions = {
  actionType: PlanCardClickEvent['actionType'];
  hasFixedRecurrence?: boolean;
  paymentRecurrence?: PricePeriodicity;
  trackingContext?: UpgradeConfirmModalTrackingContext;
  /** Actual subscription plan before the switch; tier card plans overwrite currentPlanId. */
  previousPlanId?: string;
};

export abstract class UpgradeDowngradeBaseModel {
  public abstract openDialog(
    cardPlan: ConcretePlan,
    options: DialogOptions,
    isV2?: boolean,
  ): MatDialogRef<DialogGenericType, UpgradeBillingResult>;

  public abstract handleError(err: unknown, cardPlan: ConcretePlan): void;

  protected dialogRef: MatDialogRef<DialogGenericType, UpgradeBillingResult>;
  protected dialogTrackingContext?: UpgradeConfirmModalTrackingContext;
  protected previousPlanIdForTracking?: string;

  protected billingActions: BillingActionsService;
  protected billingService: BillingService;
  protected companyService: CompanyService;
  protected matDialog: MatDialog;
  protected store: Store;
  protected translateService: TranslateService;
  protected growthBookService: GrowthBookService;
  protected approvalsPlanService: ApprovalsPlanService;
  protected upsellUpgradeContext: UpsellUpgradeContextService;
  protected segment: SegmentService;
  protected unlockInsightsUpgradeTracking: UnlockInsightsUpgradeTrackingService;

  constructor(
    injector: Injector,
  ) {
    this.billingActions = injector.get(BillingActionsService);
    this.billingService = injector.get(BillingService);
    this.companyService = injector.get(CompanyService);
    this.approvalsPlanService = injector.get(ApprovalsPlanService);
    this.growthBookService = injector.get(GrowthBookService);
    this.upsellUpgradeContext = injector.get(UpsellUpgradeContextService);
    this.segment = injector.get(SegmentService);
    this.unlockInsightsUpgradeTracking = injector.get(UnlockInsightsUpgradeTrackingService);
    this.matDialog = injector.get(MatDialog);
    this.store = injector.get(Store);
    this.translateService = injector.get(TranslateService);
  }

  protected captureDialogTrackingContext(trackingContext?: UpgradeConfirmModalTrackingContext): void {
    this.dialogTrackingContext = trackingContext;
  }

  protected capturePreviousPlanIdForTracking(explicitPlanId?: string): void {
    this.previousPlanIdForTracking = explicitPlanId
      ?? this.store.selectSnapshot(NewBillingState.updatedBillingDetails)?.currentPlanId;
  }

  protected handleSwitchPlan(cardPlan: ConcretePlan, actionType: PlanCardClickEvent['actionType']) {
    this.dialogRef.componentInstance.errorMsg = null;
    this.dialogRef.componentInstance.isLoadingAction = true;
    const previousPlanId = this.previousPlanIdForTracking
      ?? cardPlan.billingApiData?.billingDetails?.currentPlanId;

    this.billingService.modifySubscriptionPlan({ planId: cardPlan.planId }).pipe(
      take(1),
      switchMap(() => this.billingService.getBillingDetails('no-cache')),
      tap(() => this.dialogRef.componentInstance.isLoadingAction = false),
    ).subscribe({
      next: async (billingDetails) => {
        await this.handleSuccess(billingDetails, actionType, cardPlan.planId);
        this.trackPlanUpgradedIfNeeded(actionType, previousPlanId, billingDetails.currentPlanId);
        this.dialogRef.close({ didChangePlan: true });
      },
      error: (err: unknown) => {
        this.dialogRef.componentInstance.isLoadingAction = false;
        this.handleError(err, cardPlan);
      },
      complete: () => this.dialogRef.componentInstance.isLoadingAction = false,
    });
  }

  private trackPlanUpgradedIfNeeded(
    actionType: PlanCardClickEvent['actionType'],
    previousPlanId: string | undefined,
    currentPlanId: string,
  ): void {
    if (actionType !== 'upgrade') {
      return;
    }

    const upsellname = this.dialogTrackingContext?.upsellname;
    if (!upsellname) {
      return;
    }

    const origin = upsellname === UNLOCK_INSIGHTS_UPSELL_NAME
      ? UNLOCK_INSIGHTS_PLAN_UPGRADE_ORIGIN_UPSELL
      : 'upsell';

    this.segment.trackInAppEvents('Plan Upgraded', {
      from: previousPlanId,
      to: currentPlanId,
      origin,
      upgradedfrom: upsellname,
    });
    // Billing page also listens to UpdateBillingDetails; skip its duplicate Plan Upgraded.
    this.unlockInsightsUpgradeTracking.markPlanUpgradedTrackedByDialog(previousPlanId, currentPlanId);
  }

  protected async handleSuccess(
    billingDetails: BillingDetailsApiData,
    _actionType: PlanCardClickEvent['actionType'] = 'upgrade',
    planId?: string,
  ) {
    this.store.dispatch(new UpdateBillingDetails(billingDetails));
    await this.syncCompanySettingsForPlan(planId ?? billingDetails.currentPlanId);
  }

  protected getPlanDerivedCompanySettings(planId: string): Partial<CompanySettings> {
    return derivePlanCompanySettings(planId);
  }

  private async syncCompanySettingsForPlan(planId: string): Promise<void> {
    const extraChanges = this.upsellUpgradeContext.consumePendingSettingChanges();
    const shouldSync = isUpsellSetupFlowEnabled(this.growthBookService)
      || (!!extraChanges && Object.keys(extraChanges).length > 0);
    if (!shouldSync) {
      return;
    }

    const sync$ = extraChanges && Object.keys(extraChanges).length > 0
      ? this.companyService.syncCompanySettingsForPlan(planId, extraChanges)
      : this.companyService.syncCompanySettingsForPlan(planId);
    await lastValueFrom(sync$);

    if (isBasicPlanId(planId)) {
      void this.approvalsPlanService.resetNeedApprovalUsersOnBasicPlan();
    }
  }
}

